import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react'
import { View, StyleSheet, TouchableOpacity, useWindowDimensions, FlatList, ScrollView } from 'react-native'
import { Icon, Text, Button, Toggle, CheckBox, useTheme } from '@ui-kitten/components'
import StyledTextInput from './StyledTextInput'
import Divider from './Divider'
import PlatformDependantSelect from './PlatformDependantSelect'
import { useTranslation } from 'react-i18next'
import { addDecimalAmounts, parseDecimalAmount } from '../utils/decimalInput'
/**
 * A component that renders either a table row (on wide screens) or a card (on narrow screens)
 * @param {Object} props
 * @param {Array} props.data - Array of items to render
 * @param {Array} props.fields - Array of field configurations
 * @param {boolean} props.disabled - Whether inputs should be disabled
 * @param {number} props.breakpoint - The breakpoint at which the table row is rendered
 * @param {Object} props.styles - Additional styles to apply
 * @param {Function} props.renderFooter - Optional footer component
 * @param {Function} props.renderHeader - Optional header component
 * @param {Function} props.keyExtractor - Function to extract unique key for each item
 * @param {Function} props.ListEmptyComponent - Optional component to render when data is empty
 * @param {boolean} props.scrollEnabled - Whether the list should be scrollable (inside scroll view or not)
 * @param {Function|string} props.itemTestID - Optional testID for each row container, (item, index) => string
 *
 * Each field may carry a `testID` (string, or (item, index) => string) which is applied to the
 * field's interactive control so e2e can address a specific row's control.
 *
 * ⚠️ For the `input`/`quantity` types the control is a UI Kitten <Input>, which does NOT pass
 * testID through verbatim — it renders `@<testID>/input` on the inner TextInput and
 * `@<testID>/container` on the wrapper (input.component.js). So an exact-match locator such as
 * Playwright's getByTestId('my-id') finds NOTHING for those two types; target
 * `[data-testid="@my-id/input"]` instead. checkbox/toggle/iconButton render the id verbatim.
 */
export default forwardRef(function AdaptiveTableCard({
  data,
  fields,
  disabled = false,
  breakpoint = 700,
  styles: additionalStyles = {},
  renderFooter,
  renderHeader,
  keyExtractor = (item) => item.id,
  ListEmptyComponent,
  scrollEnabled = false,
  itemTestID,
}, ref) {
  const { width } = useWindowDimensions()
  const theme = useTheme()
  const [userMode, setUserMode] = useState(null) // null = auto, 'wide' or 'card' for manual
  const [wideScrollMode, setWideScrollMode] = useState(true) // false = wrap, true = scroll
  const isWideScreen = userMode !== null ? userMode === 'wide' : width > breakpoint
  const { t } = useTranslation()
  const flatListRef = useRef(null)
  const inputRefs = useRef({})

  useImperativeHandle(ref, () => ({
    scrollToIndex: (index) => {
      if (flatListRef.current && data.length > index) {
        flatListRef.current.scrollToIndex({ index, animated: true, viewPosition: 0.5 });
      }
    },
    focusField: (index, fieldKey) => {
      const refKey = `${index}-${fieldKey}`;
      setTimeout(() => {
        if (inputRefs.current[refKey] && inputRefs.current[refKey].focus) {
          inputRefs.current[refKey].focus();
        }
      }, 100);
    }
  }));
  
  // Helper function to determine if a field should be disabled
  const isFieldDisabled = (field, item) => {
    if (disabled) return !field.undisable
    if (field.isDisabled && field.isDisabled(item)) return true
    return false
  }

  // The −/+ buttons of a `quantity` field. A quantity box holds TEXT while the merchant types
  // ('240', '1.'), and `'240' + 1` is the string '2401' — a tap on + turned 240 into 2401 (ATL-1633).
  // Step the NUMBER the box spells; an empty box is 0; an unreadable one is left for the screen's
  // strict save-time parse to refuse, never stepped into a believable value.
  const stepQuantity = (field, index, item, delta) => {
    const raw = item[field.key]
    const current = raw === undefined || raw === null || raw === '' ? 0 : parseDecimalAmount(raw)
    if (current === null) return
    const next = addDecimalAmounts(current, delta)
    if (next === null) return
    field.onChange(index, next)
  }

  // testID may be a plain string or a function of the row, so rows can be addressed individually
  const resolveTestID = (testID, item, index) => (
    typeof testID === 'function' ? testID(item, index) : testID
  )

  const renderModeSwitch = () => (
    <View style={[styles.modeSwitchContainer, additionalStyles.modeSwitchContainer]}>
      <Text category='c1'>{t('viewModeSwitch')}</Text>
      <Button
        status={isWideScreen ? 'primary' : 'basic'}
        accessoryLeft={(props) => <Icon {...props} name='arrow-forward-outline'/>}
        onPress={() => setUserMode('wide')}
        size='small'
        appearance='ghost'
      />
      <Button
        status={!isWideScreen ? 'primary' : 'basic'}
        accessoryLeft={(props) => <Icon {...props} name='arrow-downward-outline'/>}
        onPress={() => setUserMode('card')}
        size='small'
        appearance='ghost'
      />
      {/* {isWideScreen && (
        <View style={{flexDirection: 'row', gap: 5, borderWidth: 1, borderColor: colors.lightGrey, borderRadius: 5}}>
          <Button
            appearance='ghost'
            size='small'
            status={wideScrollMode ? 'primary' : 'basic'}
            accessoryLeft={(props) => <Icon {...props} name='arrow-ios-forward-outline' />}
            onPress={() => setWideScrollMode(true)}
          />
          <Button
            appearance='ghost'
            size='small'
            status={!wideScrollMode ? 'primary' : 'basic'}
            accessoryLeft={(props) => <Icon {...props} name='corner-down-left-outline' />}
            onPress={() => setWideScrollMode(false)}
          />
        </View>
      )} */}
    </View>
  )

  const renderField = (field, item, index, isWideScreen) => {
    if (field.type === 'text') {
      if (isWideScreen) {
        return (
          <View style={[field.columnStyle === 'nameColumn' ? styles.nameColumn : styles.textColumn, additionalStyles[field.columnStyle], styles.rightMargin]}>
            <Text category={field.textCategory || 'p1'} style={[field.columnStyle === 'nameColumn' ? styles.nameColumnText : styles.tableText, additionalStyles.tableText]}>
              {field.formatter ? field.formatter(field.getValue ? field.getValue(item) : item[field.key], item) : (field.getValue ? field.getValue(item) : item[field.key])}
            </Text>
            <Text category='c1' appearance='hint' style={[field.columnStyle === 'nameColumn' ? styles.nameColumnText : styles.tableText, additionalStyles.tableText]}>
              {field.title}
            </Text>
          </View>
        )
      }

      if (field.columnStyle === 'nameColumn') {
        return (
          <Text category='s1' style={styles.nameFieldCard}>
            {field.formatter ? field.formatter(field.getValue ? field.getValue(item) : item[field.key], item) : (field.getValue ? field.getValue(item) : item[field.key])}
          </Text>
        )
      }

      return (
        <Text category={field.textCategory || 'c1'} style={styles.cardText}>
          {field.title}: {field.formatter ? field.formatter(field.getValue ? field.getValue(item) : item[field.key], item) : (field.getValue ? field.getValue(item) : item[field.key])}
        </Text>
      )
    }

    if (field.type === 'input' || field.type === 'quantity') {
      const input = field.type === 'quantity' ? (
        <View style={isWideScreen ? styles.quantityColumn : styles.quantityField}>
          <TouchableOpacity disabled={isFieldDisabled(field, item)} onPress={() => stepQuantity(field, index, item, -1)} style={styles.changeButtonLeft}>
            <Icon name='minus-circle-outline' fill={theme['text-hint-color']} height={30} width={30} />
          </TouchableOpacity>
          <StyledTextInput
            ref={ref => inputRefs.current[`${index}-${field.key}`] = ref}
            testID={resolveTestID(field.testID, item, index)}
            value={item[field.key]?.toString()}
            onChangeText={text => field.onChange(index, text.replace(/,/g, '.'))}
            keyboardType='decimal-pad'
            style={isWideScreen ? styles.tableInput : styles.quantityInput}
            returnKeyType='done'
            disabled={isFieldDisabled(field, item)}
            selectTextOnFocus={true}
            controlledInput={true}
            caption={isWideScreen 
              ? (field.title + (field.caption 
                ? ' (' + (typeof field.caption === 'function' 
                  ? field.caption(item) 
                  : field.caption) + ')' 
                : ''))
              : (field.caption 
                ? ' (' + (typeof field.caption === 'function' 
                  ? field.caption(item) 
                  : field.caption) + ')' 
                : '')}
          />
          <TouchableOpacity disabled={isFieldDisabled(field, item)} onPress={() => stepQuantity(field, index, item, 1)} style={styles.changeButtonRight}>
            <Icon name='plus-circle-outline' fill={theme['text-hint-color']} height={30} width={30} />
          </TouchableOpacity>
        </View>
      ) : (
        <StyledTextInput
          ref={ref => inputRefs.current[`${index}-${field.key}`] = ref}
          testID={resolveTestID(field.testID, item, index)}
          value={(field.getValue ? field.getValue(item) : item[field.key])?.toString()}
          onChangeText={text => field.onChange(index, text.replace(/,/g, '.'))}
          keyboardType={field.keyboardType || 'default'}
          style={[isWideScreen ? styles.tableInput : styles.cardInput, isWideScreen && styles.rightMargin]}
          returnKeyType='done'
          disabled={isFieldDisabled(field, item)}
          selectTextOnFocus={true}
          caption={isWideScreen 
            ? (field.title + (field.caption 
              ? ' (' + (typeof field.caption === 'function' 
                ? field.caption(item) 
                : field.caption) + ')' 
              : ''))
            : (field.caption 
              ? ' (' + (typeof field.caption === 'function' 
                ? field.caption(item) 
                : field.caption) + ')' 
              : '')}
          controlledInput={field.controlledInput}
        />
      )

      if (!isWideScreen) {
        return (
          <View style={styles.itemRow}>
            <Text style={styles.miniLabel}>{field.title}</Text>
            {input}
          </View>
        )
      }

      return input
    }

    if (field.type === 'select') {
      const select = (
        <PlatformDependantSelect
          data={field.options}
          selectedOption={field.selectedOption ? field.selectedOption(item) : item[field.key]}
          onSelect={value => field.onChange(index, value)}
          disabled={isFieldDisabled(field, item)}
          style={isWideScreen ? styles.tableSelect : styles.cardSelect}
          caption={isWideScreen ? field.title : undefined}
        />
      )

      if (!isWideScreen) {
        return (
          <View style={styles.itemRow}>
            <Text style={styles.miniLabel}>{field.title}</Text>
            {select}
          </View>
        )
      }

      return (
        <View style={[styles.selectColumn, additionalStyles.selectColumn, styles.rightMargin]}>
          {select}
        </View>
      )
    }

    if (field.type === 'toggle' || field.type === 'checkbox') {
      const isCheckbox = field.type === 'checkbox'
      const Control = isCheckbox ? CheckBox : Toggle
      const control = (
        <Control
          checked={field.getValue ? field.getValue(item) : item[field.key]}
          onChange={checked => field.onChange(index, checked)}
          disabled={isFieldDisabled(field, item)}
          style={isWideScreen && !isCheckbox ? styles.tableToggle : styles.cardToggle}
          testID={resolveTestID(field.testID, item, index)}
        />
      )

      if (!isWideScreen) {
        return (
          <View style={styles.itemRow}>
            <Text style={styles.miniLabel}>{field.title}</Text>
            <View style={styles.cardToggleContainer}>
              {control}
            </View>
          </View>
        )
      }

      return (
        <View style={[
          isCheckbox ? styles.checkboxColumn : styles.toggleColumn,
          additionalStyles[isCheckbox ? 'checkboxColumn' : 'toggleColumn'],
          styles.rightMargin,
        ]}>
          {control}
          {field.title && <Text category='c1' appearance='hint'>{field.title}</Text>}
        </View>
      )
    }

    if (field.type === 'iconButton') {
      const button = (
        <TouchableOpacity
          onPress={() => field.onPress(item, index)}
          style={[styles.iconButton, additionalStyles.iconButton]}
          disabled={isFieldDisabled(field, item)}
          testID={resolveTestID(field.testID, item, index)}
        >
          <Icon 
            width={field.iconSize || 20} 
            height={field.iconSize || 20} 
            fill={field.iconColor || theme['text-hint-color']}
            name={field.iconName}
          />
        </TouchableOpacity>
      )

      if (!isWideScreen) {
        return button
      }

      return (
        <View style={[styles.iconButtonColumn, additionalStyles.iconButtonColumn, styles.rightMargin]}>
          {button}
          {field.title && <Text category='c1' appearance='hint'>{field.title}</Text>}
        </View>
      )
    }
  }

  const renderItem = ({item, index}) => {
    if (isWideScreen) {
      // Separate icon buttons from other fields
      const regularFields = fields.filter(f => f.type !== 'iconButton')
      const iconButtons = fields.filter(f => f.type === 'iconButton')

      const tableContent = (
        <View style={[styles.tableRow, additionalStyles.tableRow, wideScrollMode && styles.tableRowNoWrap]}>
          {regularFields.map((field, fieldIndex) => (
            <View key={fieldIndex}>
              {renderField(field, item, index, true)}
            </View>
          ))}
          {/* Render icon buttons at the end */}
          {iconButtons.length > 0 && (
            <View style={[styles.iconButtonsContainer, additionalStyles.iconButtonsContainer]}>
              {iconButtons.map((field, fieldIndex) => (
                <View key={`icon-${fieldIndex}`}>
                  {renderField(field, item, index, true)}
                </View>
              ))}
            </View>
          )}
        </View>
      )

      return (
        <View
          style={[styles.fieldContainer, { backgroundColor: theme['background-basic-color-1'] }, additionalStyles.fieldContainer]}
          testID={resolveTestID(itemTestID, item, index)}
        >
          {wideScrollMode ? (
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {tableContent}
            </ScrollView>
          ) : (
            tableContent
          )}
        </View>
      )
    }

    // For non-wide mode, find name field and icon buttons
    const nameField = fields.find(f => f.columnStyle === 'nameColumn')
    const iconButtons = fields.filter(f => f.type === 'iconButton')
    const otherFields = fields.filter(f => f.type !== 'iconButton' && f.columnStyle !== 'nameColumn')

    return (
      <View
        style={[styles.fieldContainer, { backgroundColor: theme['background-basic-color-1'] }, additionalStyles.fieldContainer]}
        testID={resolveTestID(itemTestID, item, index)}
      >
        {/* Render name field with icon buttons */}
        {nameField && (
          <>
            <View style={styles.nameRowContainer}>
              {renderField(nameField, item, index, false)}
              {iconButtons.length > 0 && (
                <View style={styles.nameRowIconButtons}>
                  {iconButtons.map((field, fieldIndex) => (
                    <View key={`icon-${fieldIndex}`}>
                      {renderField(field, item, index, false)}
                    </View>
                  ))}
                </View>
              )}
            </View>
            <Divider style={{height: 1, width: '100%'}} /> 
          </>
        )}
        {otherFields.map((field, fieldIndex) => (
          <View key={fieldIndex}>
            {renderField(field, item, index, false)}
          </View>
        ))}
      </View>
    )
  }

  return (
    <FlatList
      ref={flatListRef}
      data={data}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      getItemLayout={(data, index) => ({
        length: 120, // Approximate height of each item
        offset: 120 * index,
        index,
      })}
      onScrollToIndexFailed={info => {
        setTimeout(() => {
          if (flatListRef.current) {
            flatListRef.current.scrollToOffset({
              offset: info.averageItemLength * info.index,
              animated: true
            });
          }
        }, 100);
      }}
      ListHeaderComponent={
        <>
          {renderHeader && renderHeader()}
          {data?.length > 0 && renderModeSwitch()}
        </>
      }
      ListFooterComponent={renderFooter}
      ListEmptyComponent={ListEmptyComponent}
      scrollEnabled={scrollEnabled}
      style={[styles.list, additionalStyles.list]}
    />
  )
});

const styles = StyleSheet.create({
  fieldContainer: {
    alignSelf: 'stretch',
    marginVertical: 6,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginVertical: 5,
  },
  miniLabel: {
    flex: 1,
    minWidth: 80,
    flexShrink: 2,
    marginRight: 10,
  },
  nameFieldCard: {
    flex: 1,
  },
  cardInput: {
    flex: 2,
    minWidth: 120,
  },
  quantityField: {
    flex: 2,
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 120,
  },
  quantityInput: {
    flex: 1,
    minWidth: 80,
  },
  cardText: {
    marginVertical: 4,
  },
  changeButtonLeft: {
    marginRight: 3,
    alignSelf: 'center',
    marginBottom: 10,
  },
  changeButtonRight: {
    marginLeft: 3,
    alignSelf: 'center',
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  tableRowNoWrap: {
    flexWrap: 'nowrap',
  },
  quantityColumn: {
    marginRight: 10,
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 150,
    maxWidth: 250,
  },
  tableInput: {
    flex: 1,
    minWidth: 90,
    flexShrink: 1,
    maxWidth: 250,
    // alignSelf: 'center',
  },
  tableText: {
    textAlign: 'center',
  },
  textColumn: {
    minWidth: 100,
  },
  nameColumn: {
    width: 180
  },
  nameColumnText: {
    textAlign: 'left',
  },
  rightMargin: {
    marginRight: 5,
  },
  selectColumn: {
    flex: 1,
    minWidth: 150,
    maxWidth: 250,
  },
  tableSelect: {
    flex: 1,
  },
  cardSelect: {
    flex: 2,
    minWidth: 120,
  },
  modeSwitchContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 5,
    alignItems: 'center',
  },
  toggleColumn: {
    minWidth: 150,
    maxWidth: 220,
    justifyContent: 'center',
  },
  checkboxColumn: {
    minWidth: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardToggle: {
    // flex: 1,
  },
  cardToggleContainer: {
    flex: 2,
    minWidth: 120,
    alignItems: 'flex-start',
  },
  tableToggle: {
    height: 40
  },
  iconButtonColumn: {
    minWidth: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconButton: {
    padding: 5,
  },
  iconButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginLeft: 'auto',
    paddingLeft: 10,
  },
  nameRowContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  nameRowIconButtons: {
    flexDirection: 'row',
    gap: 5,
    marginLeft: 10,
  },
}) 